import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, CheckCircle, Eye, MessageSquare, AlertTriangle, Shield, DollarSign, Activity, Clock } from 'lucide-react';
import api from '../../lib/api';

const SEVERITY_COLORS = {
  CRITICAL: 'bg-red-600 text-white',
  HIGH: 'bg-orange-500 text-white',
  MEDIUM: 'bg-yellow-500 text-black',
  LOW: 'bg-slate-400 text-white',
};

const STATUS_COLORS = {
  open: 'bg-red-900/30 text-red-400 border-red-800',
  acknowledged: 'bg-yellow-900/30 text-yellow-400 border-yellow-800',
  resolved: 'bg-green-900/30 text-green-400 border-green-800',
};

const TYPE_ICONS = { operational: Activity, financial: DollarSign, compliance: Shield };

export default function SmartAlertDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAlert = async () => {
    try {
      const res = await api.get(`/smart-alerts/${id}`);
      if (res.data?.data) setAlert(res.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchAlert(); }, [id]);

  const handleAcknowledge = async () => {
    setSubmitting(true);
    await api.post(`/smart-alerts/${id}/acknowledge`);
    await fetchAlert();
    setSubmitting(false);
  };

  const handleResolve = async () => {
    setSubmitting(true);
    await api.post(`/smart-alerts/${id}/resolve`, { note: 'Resolved via dashboard' });
    await fetchAlert();
    setSubmitting(false);
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSubmitting(true);
    await api.post(`/smart-alerts/${id}/note`, { text: noteText });
    setNoteText('');
    await fetchAlert();
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <p className="text-slate-500">Alert not found</p>
      </div>
    );
  }

  const Icon = TYPE_ICONS[alert.alert_type] || AlertTriangle;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="smart-alert-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Back + Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/smart-alerts')} className="text-slate-400" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>

        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-[#12121a] border border-[#1e1e2e]">
            <Icon className="h-6 w-6 text-blue-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold" data-testid="alert-title">{alert.title}</h1>
              <Badge className={`${SEVERITY_COLORS[alert.risk_level]} text-xs`}>{alert.risk_level}</Badge>
              <Badge variant="outline" className={`text-xs ${STATUS_COLORS[alert.status]}`}>{alert.status}</Badge>
            </div>
            <p className="text-sm text-slate-400 mt-1 capitalize">{alert.alert_type} — {alert.module}</p>
          </div>
        </div>

        {/* Actions */}
        {alert.status !== 'resolved' && (
          <div className="flex gap-2">
            {alert.status === 'open' && (
              <Button onClick={handleAcknowledge} disabled={submitting} variant="outline" className="border-yellow-700 text-yellow-400 hover:bg-yellow-900/30" data-testid="acknowledge-btn">
                <Eye className="h-4 w-4 mr-2" /> Acknowledge
              </Button>
            )}
            <Button onClick={handleResolve} disabled={submitting} variant="outline" className="border-green-700 text-green-400 hover:bg-green-900/30" data-testid="resolve-btn">
              <CheckCircle className="h-4 w-4 mr-2" /> Resolve
            </Button>
          </div>
        )}

        {/* Detail Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#12121a] border-[#1e1e2e]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Why Triggered</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-slate-200" data-testid="alert-reason">{alert.reason}</p></CardContent>
          </Card>
          <Card className="bg-[#12121a] border-[#1e1e2e]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Evidence</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-slate-200" data-testid="alert-evidence">{alert.evidence}</p></CardContent>
          </Card>
        </div>

        <Card className="bg-[#12121a] border-[#1e1e2e]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Recommended Action</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-blue-300" data-testid="alert-action">{alert.recommended_action}</p></CardContent>
        </Card>

        {/* Metadata */}
        <Card className="bg-[#12121a] border-[#1e1e2e]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Details</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Metric Value</span><span className="text-slate-200 font-mono">{alert.metric_value}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Created</span><span className="text-slate-300">{alert.created_at ? new Date(alert.created_at).toLocaleString() : '—'}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Last Scanned</span><span className="text-slate-300">{alert.last_scanned_at ? new Date(alert.last_scanned_at).toLocaleString() : '—'}</span></div>
            {alert.acknowledged_by && <div className="flex justify-between"><span className="text-slate-500">Acknowledged By</span><span className="text-yellow-400">{alert.acknowledged_by}</span></div>}
            {alert.resolved_by && <div className="flex justify-between"><span className="text-slate-500">Resolved By</span><span className="text-green-400">{alert.resolved_by}</span></div>}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-[#12121a] border-[#1e1e2e]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Notes & History ({alert.notes?.length || 0})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {(alert.notes || []).map((note, idx) => (
              <div key={idx} className="flex gap-2 text-sm border-l-2 border-slate-700 pl-3">
                <Clock className="h-3.5 w-3.5 text-slate-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-slate-300">{note.text}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{note.by === 'system' ? 'System' : note.by?.slice(0, 8)} — {note.at ? new Date(note.at).toLocaleString() : ''}</p>
                </div>
              </div>
            ))}

            {alert.status !== 'resolved' && (
              <div className="flex gap-2 mt-4">
                <input
                  type="text"
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 bg-[#0a0a0f] border border-[#1e1e2e] rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-600"
                  onKeyDown={e => { if (e.key === 'Enter') handleAddNote(); }}
                  data-testid="note-input"
                />
                <Button onClick={handleAddNote} disabled={submitting || !noteText.trim()} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-note-btn">
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
